import { useState, useRef, useMemo, useCallback } from "react";
import { Box } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { PrimaryFormActionButton } from "../../../Features/Table/GenericDraggableTable/TableStyles";
import GenericDraggableTableView from "../../../Features/Table/GenericDraggableTable";
import SearchInput from "../../Overview/components/SearchInput";
import { CanCreate } from "../../../Components/PermissionGuards";
import { FEATURE_CODES } from "../../../Static/FeatureMapping";
import CreateEditLocationDialog from "./CreateEditLocationDialog";
import DeleteLocationDialog from "./DeleteLocationDialog";
import useOrgLocations from "./hooks/useOrgLocations";
import useLocationColumns from "./hooks/useLocationColumns";

const SupportedLocationsContent = () => {
  const { locationLevels, locations, isLoading, refetch } = useOrgLocations();

  const [searchText, setSearchText] = useState("");
  const [selectedFilters, setSelectedFilters] = useState({});
  const [pageNumber, setPageNumber] = useState(1);
  const [createEditOpen, setCreateEditOpen] = useState(false);
  const [editLocation, setEditLocation] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const debounceRef = useRef(null);

  const handleSearchChange = useCallback((val) => {
    setSearchText(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setPageNumber(1), 200);
  }, []);

  const handleApplyFilters = useCallback((filters) => {
    setSelectedFilters(filters);
    setPageNumber(1);
  }, []);

  const handleEdit = useCallback((row) => {
    setEditLocation(row);
    setCreateEditOpen(true);
  }, []);

  const handleDeleteRequest = useCallback((row) => {
    setDeleteTarget(row);
  }, []);

  const tableColumns = useLocationColumns(locationLevels, handleEdit, handleDeleteRequest);

  const filteredLocations = useMemo(() => {
    let result = locations;

    const activeFilters = Object.entries(selectedFilters).filter(
      ([, vals]) => Array.isArray(vals) && vals.length > 0
    );
    if (activeFilters.length > 0) {
      result = result.filter((row) =>
        activeFilters.every(([key, vals]) => {
          const rowVal = (row[key] || "").toLowerCase();
          return vals.some((v) => v.toLowerCase() === rowVal);
        })
      );
    }

    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      result = result.filter((row) =>
        locationLevels.some((lvl) =>
          (row[lvl.level] || "").toLowerCase().includes(lower)
        )
      );
    }

    return result;
  }, [locations, searchText, locationLevels, selectedFilters]);

  const pagedLocations = useMemo(() => {
    const start = (pageNumber - 1) * 20;
    return filteredLocations.slice(start, start + 20);
  }, [filteredLocations, pageNumber]);

  const tableFilters = useMemo(() => {
    const filters = {};
    locationLevels.forEach((lvl) => {
      const seenLower = new Set();
      const uniqueVals = locations
        .map((row) => row[lvl.level])
        .filter(Boolean)
        .filter((v) => {
          const key = v.toLowerCase();
          if (seenLower.has(key)) return false;
          seenLower.add(key);
          return true;
        })
        .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
      if (uniqueVals.length > 0) {
        filters[lvl.level] = {
          key: lvl.level,
          label: lvl.type,
          displayType: "multiSelectDropdown",
          values: uniqueVals.map((v) => ({ id: v, value: v })),
        };
      }
    });
    return filters;
  }, [locationLevels, locations]);

  const tableHeaderActions = useMemo(() => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box sx={{ width: 320, "& .MuiInputBase-input": { paddingTop: "8px", paddingBottom: "8px" } }}>
        <SearchInput
          value={searchText}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Search country, state, city, depot..."
        />
      </Box>
      <CanCreate feature={FEATURE_CODES.SUPPORTED_LOCATIONS}>
        <PrimaryFormActionButton
          startIcon={<AddIcon />}
          onClick={() => {
            setEditLocation(null);
            setCreateEditOpen(true);
          }}
        >
          Add Location
        </PrimaryFormActionButton>
      </CanCreate>
    </Box>
  ), [searchText, handleSearchChange]);

  return (
    <>
      {/* Wrapper hides Ant Design's native sort caret; our TableHeader icons handle sort state */}
      <Box
        sx={{
          "& .ant-table-column-sorter": { display: "none" },
          "& .ant-table-column-has-sorters .ant-table-cell-content": { cursor: "pointer" },
        }}
      >
        <GenericDraggableTableView
          title="Supported Locations"
          count={filteredLocations.length}
          showPrimaryButton={false}
          tableColumns={tableColumns}
          tableData={pagedLocations}
          isLoading={isLoading}
          pageNumber={pageNumber}
          rowsPerPage={20}
          total={filteredLocations.length}
          onPageChange={setPageNumber}
          applySort={() => {}}
          filters={tableFilters}
          selectedFilters={selectedFilters}
          applyFilters={handleApplyFilters}
          disableSearch={true}
          disableDownloads={true}
          disableColumnSettings={true}
          emptyMessage="No locations found"
          disablePagination={false}
          customRightActions={true}
          customRightActionsView={tableHeaderActions}
        />
      </Box>

      <CreateEditLocationDialog
        open={createEditOpen}
        mode={editLocation ? "edit" : "create"}
        location={editLocation}
        locationLevels={locationLevels}
        locations={locations}
        onClose={() => {
          setCreateEditOpen(false);
          setEditLocation(null);
        }}
        onSuccess={() => {
          setCreateEditOpen(false);
          setEditLocation(null);
          refetch(selectedFilters);
        }}
      />

      <DeleteLocationDialog
        target={deleteTarget}
        locationLevels={locationLevels}
        onClose={() => setDeleteTarget(null)}
        onSuccess={() => {
          setDeleteTarget(null);
          refetch(selectedFilters);
        }}
      />
    </>
  );
};

export default SupportedLocationsContent;
